import { createWithEqualityFn as create } from 'zustand/traditional';
import { getSocketURL } from '@/shared/api/get-socket-url';
import type { MainWsMessage } from '@/shared/api/websocket';
import type { WsTypes } from '@/shared/api/websocket-events';
import { runSocketCallback } from './run-callback';

type MessageHandler = (message: MainWsMessage) => unknown;

interface WebSocketStore {
  ws: WebSocket | null;
  isConnected: boolean;
  reconnectAttempts: number;
  handlers: Partial<
    Record<WsTypes, { name?: string; callback: MessageHandler }[]>
  >;
  connect: () => void;
  disconnect: () => void;
  retainConnection: () => () => void;
  addHandler: (type: WsTypes, callback: MessageHandler, name?: string) => void;
  removeHandler: (type: WsTypes, callback: MessageHandler) => void;
}

const detachSocket = (ws: WebSocket) => {
  ws.onopen = null;
  ws.onmessage = null;
  ws.onerror = null;
  ws.onclose = null;
};

export const createWebSocketStore = () =>
  create<WebSocketStore>((set, get) => {
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let shouldReconnect = false;
    let connectionUsers = 0;

    const clearReconnectTimer = () => {
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      reconnectTimer = null;
    };

    const scheduleReconnect = () => {
      if (!shouldReconnect) return;

      clearReconnectTimer();
      const reconnectAttempts = get().reconnectAttempts + 1;
      const maxDelay = Math.min(
        1000 * 2 ** Math.min(reconnectAttempts, 5),
        30_000,
      );
      // Equal jitter spreads clients across the latter half of each backoff window.
      const delay = maxDelay * (0.5 + Math.random() * 0.5);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        initializeWebSocket();
      }, delay);
      set({ reconnectAttempts });
    };

    const initializeWebSocket = () => {
      if (!shouldReconnect || get().ws) return;

      let ws: WebSocket;
      try {
        ws = new WebSocket(getSocketURL({ path: 'api/auth/v3/ws/consume' }));
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        scheduleReconnect();
        return;
      }

      const isCurrentSocket = () => shouldReconnect && get().ws === ws;

      ws.onopen = () => {
        if (!isCurrentSocket()) return;
        clearReconnectTimer();
        set({ isConnected: true, reconnectAttempts: 0 });
      };

      ws.onmessage = (event) => {
        if (!isCurrentSocket()) return;

        let message: MainWsMessage;
        try {
          message = JSON.parse(event.data);
          const handlers = get().handlers[message.type] ?? [];
          for (const { callback } of handlers) {
            void runSocketCallback(() => callback(message));
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        if (isCurrentSocket()) console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        if (!isCurrentSocket()) return;
        detachSocket(ws);
        set({ ws: null, isConnected: false });
        scheduleReconnect();
      };

      set({ ws });
    };

    return {
      ws: null,
      isConnected: false,
      reconnectAttempts: 0,
      handlers: {},

      connect: () => {
        if (shouldReconnect) return;
        shouldReconnect = true;
        initializeWebSocket();
      },

      disconnect: () => {
        shouldReconnect = false;
        clearReconnectTimer();
        const ws = get().ws;
        if (ws) detachSocket(ws);
        set({ ws: null, isConnected: false, reconnectAttempts: 0 });
        ws?.close();
      },

      retainConnection: () => {
        connectionUsers++;
        if (connectionUsers === 1) get().connect();
        let released = false;
        return () => {
          if (released) return;
          released = true;
          connectionUsers--;
          // A replacement consumer or StrictMode setup can retain the socket
          // within the same commit, avoiding a needless close/open cycle.
          if (connectionUsers === 0) {
            void Promise.resolve().then(() => {
              if (connectionUsers === 0) get().disconnect();
            });
          }
        };
      },

      addHandler: (type, callback, name) => {
        const handlers = get().handlers;
        set({
          handlers: {
            ...handlers,
            [type]: [...(handlers[type] ?? []), { callback, name }],
          },
        });
      },

      removeHandler: (type, callback) => {
        const handlers = get().handlers;
        set({
          handlers: {
            ...handlers,
            [type]: (handlers[type] ?? []).filter(
              (handler) => handler.callback !== callback,
            ),
          },
        });
      },
    };
  });

export const useWebSocketStore = createWebSocketStore();

if (import.meta.hot) {
  import.meta.hot.dispose(() => useWebSocketStore.getState().disconnect());
}